import React from 'react';
import { Prompt } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Typography, Grid, TextField, Box, Divider } from '@mui/material';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SaveButton from 'common/components/SaveButton';

import { api } from 'common/frontend/api';

import { ShopPublicInfo } from 'common/types';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import { Routes, useTypedLocation } from 'routing';
import { useTranslation } from 'services/localization';

import { IndustryCategory } from 'common/services/analytics/tractionAnalytics';
import CookiePolicy from './components/CookiePolicy';
import MarketingConsent from './components/MarketingConsent';
import PrivacyPolicyUpload from './components/PrivacyPolicyUpload';
import TermsUpload from './components/TermsUpload';
import CancellationPolicy from './components/CancellationPolicy';
import { setOnboardingActionDone } from 'services/onboarding/utils';

type FormValues = {
	publicInfo: Partial<ShopPublicInfo>;
	industryCategories: IndustryCategory[];
};

const AccountLegal = () => {
	const { t } = useTranslation();
	const { state } = useTypedLocation();
	const dispatch = useDispatch();
	const publicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const defaultCookieSettings = {
		default: publicInfo?.cookieSettings?.default ?? 'ENABLE',
		embed: publicInfo?.cookieSettings?.embed ?? 'ENABLE',
	};

	const defaultValues: FormValues = {
		publicInfo,
		industryCategories: activeShopAnalytics?.industryCategories ?? [],
	};

	const form = useForm<FormValues>({
		defaultValues,
		mode: 'onChange',
	});

	const { isDirty, isValid, isSubmitting, dirtyFields } = form.formState;

	const updateOnboardingActions = () => {
		if (!!dirtyFields.publicInfo?.cancellationPolicy) {
			setOnboardingActionDone('CANCELLATION_POLICY', activeShopAnalytics);
		}
	};

	const onSubmit = async (data: FormValues) => {
		try {
			await api().shops.doc(shopId).public.update(data.publicInfo);

			if (!!dirtyFields.industryCategories && !!activeShopAnalytics?.id) {
				await api()
					.shopAnalytics.doc(activeShopAnalytics.id)
					.update({ industryCategories: data.industryCategories });
			}
			updateOnboardingActions();
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.termsAndPolicies.pageTitle', 'Terms & Policies')}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={form.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={
					state?.from === Routes.HOME_VIEW
						? { to: Routes.HOME_VIEW, title: t('common:actions.home', 'Home') }
						: { to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }
				}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">{t('common:terms.termsAndPrivacy')}</Typography>
							<Typography variant="body2" color="secondary">
								{t(
									'privacy.termsAndPrivacyInfo',
									'Give your customers the option to view your liability waiver and privacy policy',
								)}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="body2" gutterBottom>
								{t('common:terms.privacyPolicy')}
							</Typography>
							<PrivacyPolicyUpload shopId={shopId} />
							<Box mt={4} />
							<Typography variant="body2" gutterBottom>
								{t('common:terms.termsOfService')}
							</Typography>
							<TermsUpload shopId={shopId} />
						</Grid>
					</Grid>
					<Box my={4}>
						<Divider />
					</Box>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">{t('shop.marketingConsentTextLabel')}</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								control={form.control}
								name={'publicInfo.marketingConsentText'}
								render={(renderProps) => (
									<MarketingConsent
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
									/>
								)}
							/>
						</Grid>
					</Grid>
					<Box my={4}>
						<Divider />
					</Box>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">
								{t('privacy.customerPrivacy', 'Customer privacy')}
							</Typography>
							<Typography variant="body2" color="secondary">
								{t('privacy.customerPrivacyInfo', 'Give your customers control over their data')}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								control={form.control}
								name={'publicInfo.cookieSettings'}
								defaultValue={defaultCookieSettings}
								render={(renderProps) => (
									<CookiePolicy
										value={renderProps.field.value ?? defaultCookieSettings}
										onChange={renderProps.field.onChange}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
				<ContainerBox>
					<Grid id="cancellation-policy">
						<Controller
							control={form.control}
							render={(renderProps) => (
								<CancellationPolicy
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
								/>
							)}
							{...form.register('publicInfo.cancellationPolicy')}
						/>
					</Grid>
				</ContainerBox>
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h5">
								{t('account.general.receipts.header', 'Receipts')}
							</Typography>
						</Grid>
					</Grid>
					<Grid container spacing={2} mt={4}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">
								{t('account.general.receipts.customerReceipts', 'Customer payment receipts')}
							</Typography>
							<Typography variant="body2" color="secondary">
								{t(
									'account.general.receipts.customizeReceipt',
									'Customize the receipt your customers will receive',
								)}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								control={form.control}
								name={'publicInfo.receiptOpenTextfield'}
								render={(renderProps) => (
									<TextField
										label={t('account.general.receipts.additionalInfo', 'Additional information')}
										value={renderProps.field.value}
										onChange={renderProps.field.onChange}
										placeholder={
											t('common:form.typeSomethingPlaceholder', 'Type something') + '...'
										}
										InputLabelProps={{
											shrink: true,
										}}
										fullWidth
										multiline
									/>
								)}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
			</Container>
		</>
	);
};

export default AccountLegal;
